import "./HorizontalNavbar.scss";
import { setDarkMode, setLightMode } from "../../redux/themeSlice";
import { logOut, setSessionValidated } from "../../redux/userSlice";
import newRequest from "../../utils/newRequest";
import CircularButton, { CustomButtonMenu, CustomLoadingButton } from "../ui/CustomButton";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Select } from 'antd';
import { activeStatuses } from "../../utils/core";
import { MdClose, MdDarkMode, MdLightMode, MdOutlineMenuOpen } from "react-icons/md";
import CurrentUserAvatar from "../currentUserAvatar/CurrentUserAvatar";
import { useTranslation } from "react-i18next";
import { IoNotificationsSharp } from "react-icons/io5";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import { showToast } from "../../toastService";
import { motion } from "framer-motion";
import ResetPassword from "../resetPassword/ResetPassword";
import { useSocket } from "../../redux/useSocket";
import InitialsAvatar from "../InitialsAvatar/InitialsAvatar";

const HorizontalNavbar = () => {
  const { t } = useTranslation();
  const socket = useSocket();
  const { currentUser } = useSelector((state) => state.user);
  const { currentTheme } = useSelector((state) => state.theme);
  const { todoCount } = useSelector((state) => state.todo);
  const [searchInput, setSearchInput] = useState("");
  const [open, setOpen] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [todoOpen, setTodoOpen] = useState(false);
  const [currentMenuIndex, setCurrentMenuIndex] = useState(0);
  const [currentMenu, setCurrentMenu] = useState("home");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [openActions, setOpenActions] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const todoRef = useRef(null);
  const dropdownRef = useRef(null);
  const actionsRef = useRef(null);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenNotifications(false);
      }

      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setOpenActions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  const [selectedSearchFilter, setSelectedSearchFilter] = useState("everything");

  useEffect(() => {
    function handleClickOutside(event) {
      // for hiding todo
      if (todoRef.current && !todoRef.current.contains(event.target)) {
        setTodoOpen(false);
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);




  const fetchUserRoles = async (userDocid) => {
    try {
      const res = await newRequest.get(`/adUserRole/userRoles/${userDocid}`);
      setUserRoles(res.data.roles);
      setCurrentMenuIndex(100)
    } catch (err) {
      console.log(err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await newRequest.get("/note/findAll");
      setNotifications(res.data);
    } catch (err) {
      console.log(err);
    }
  };


  const handleLogout = async () => {
    try {
      // Clear UI/session-only state
      sessionStorage.removeItem("selectedMenu");
      sessionStorage.removeItem("expandedAccordion");
      sessionStorage.removeItem("menuExpanded");

      // Backend logout (invalidate token / session)
      await newRequest.get("/auth/logout");

      // Notify socket BEFORE state reset
      if (socket?.connected) {
        socket.emit("logoutUser", { socketId: socket.id });
      }

      // Redux cleanup
      dispatch(setSessionValidated(false));
      dispatch(logOut());

      // Force login screen
      navigate("/bima", { replace: true });
    } catch (err) {
      console.error("Logout failed", err);
    }
  };




  const toggleTheme = async (e) => {
    e.preventDefault();
    try {
      if (currentTheme === "light") {
        dispatch(setDarkMode());
      } else {
        dispatch(setLightMode());
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleMenuClick = (roleCode, roleComponent, roleMenus, index) => {
    try {
      setCurrentMenu(roleComponent.replace("/", ""));
      navigate(roleComponent, {
        state: { scroll: true, roleMenus },
        replace: true,
      });
      setCurrentMenuIndex(index)
    } catch (error) {
      console.log(error);
    }
  };


  const handleOptionsChange = (value) => {
    setSelectedSearchFilter(value);
  };


  const handleSearchSubmit = (e) => {
    try {
      if (e) { e.preventDefault(); }
      if (searchInput) {
        navigate('/searchResults', { replace: true, state: { searchInput: searchInput, searchFilter: selectedSearchFilter } });
      }
    } catch (error) {
      console.log(error)
    }
  };

  const handleViewDocument = async (n) => {
    try {
      if (!n) return;
      if (n.note_type === "report") {
        if (!n?.note_binary_docid) {
          showToast(t('documentNotFound'), "error");
          return;
        }
        const response = await newRequest.get(`/viewdocs/view/${n?.note_binary_docid}`, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setOpenNotifications(false);
      } else if (n.note_type === "document") {
        if (!n?.note_document_path) {
          showToast(t('pathNotFound'), "error");
          return;
        }
        navigate(`/${n?.note_document_path}`, { replace: true });
        setOpenNotifications(false);
      }
    } catch (error) {
      console.log(error)
    }
  };

  const handleOpenNotifications = async () => {
    try {
      // Optimistic update: immediately set UI state
      setUnreadCount(0);
      setOpenNotifications(prev => !prev);
      // Mark locally as read
      setNotifications(prev =>
        prev.map(n => ({ ...n, note_opened: "Y" }))
      );
      // Fire & forget API call (no waiting for user)
      updateNotificationsAsRead(notifications);
    } catch (error) {
      console.error(error);
    }
  };

  const updateNotificationsAsRead = async (notifications) => {
    try {
      const ids = notifications.map(n => n._id);
      await newRequest.post("/note/readMany", ids);
    } catch (error) {
      console.error("Failed to update notifications:", error);

      // Optional rollback for strict consistency
      // setUnreadCount(notifications.filter(n => n.note_opened === "N").length);
    }
  };


  useEffect(() => {
    if (!notifications) return;
    const unread = notifications.filter(n => n.note_opened === "N").length;
    setUnreadCount(unread);
  }, [notifications]);

  useEffect(() => {
    if (!socket) return;

    const handler = (data) => {
      // console.log("Notification:", data);
      setNotifications((prev) => [data, ...prev]);
    };

    socket.on("notification", handler);

    return () => {
      socket.off("notification", handler);
    };
  }, [socket]);


  useEffect(() => {
    // if (currentUser?.usr_entity_code) {
    //   const menus = [
    //     { _id: "home", menu_name: "Home", menu_function: "subagt" },
    //     { _id: "claims", menu_name: "Claims", menu_function: "cfm" },
    //     { _id: "payments", menu_name: "Payments", menu_function: "pmt" },
    //     { _id: "reports", menu_name: "Reports", menu_function: "crp" },
    //   ];
    //   setFacilityMenus(menus);
    //   setCurrentFacilityMenu(menus[0]);
    //   // navigate(menus[0]?.menu_function, { replace: true });
    // } else {
    //   fetchUserRoles(currentUser.usr_id);
    // }
    fetchUserRoles(currentUser.usr_id);
    fetchNotifications();
  }, [currentUser.usr_id]);

  return (
    <div className="horizontal-navbar">
      <div className="top-part">
        <div className="left-desktop">
          {currentUser?.usr_entity_code ?
            <span className="main-title">{currentUser?.usr_org_name}</span>
            :
            <div className="search-input-wrapper" style={{ width: "90%" }}>
              <Select
                defaultValue="everything"
                style={{
                  border: "none",
                  width: 150,
                  margin: "0px 2px",
                }}
                onChange={handleOptionsChange}
                options={[
                  {
                    value: 'everything',
                    label: `${t('everything')}`,
                  },
                  {
                    value: 'entities',
                    label: `${t('entities')}`,
                  },
                  {
                    value: 'receipts',
                    label: `${t('receipts')}`,
                  },
                  {
                    value: 'payments',
                    label: `${t('payments')}`,
                  },
                  {
                    value: 'claims',
                    label: `${t('claims')}`,
                  },
                ]}
              />
              <input
                className="search-input"
                style={{ borderRadius: "8px" }}
                type="text"
                placeholder={`${t('search')} ${t(selectedSearchFilter)}`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchSubmit();
                }}
              />
              {searchInput?.length > 0 && (
                <MdClose
                  onClick={(e) => setSearchInput("")}
                  className="search-input-icon"
                />
              )}
            </div>
          }
        </div>
        <div className="left-mobile">
          {/* User avatar */}
          <CurrentUserAvatar
            onClick={() => {
              try {
                setCurrentMenu("/profile");
                navigate(`/profile`, { replace: true });
                setCurrentMenuIndex(200);
              } catch (error) {
                console.log(error);
              }
            }}
          />
        </div>

        <div className="right" ref={dropdownRef}>
          {/* Notifications */}
          <span className="notification" style={{ cursor: "pointer" }}
            onClick={handleOpenNotifications}
          >
            <Badge badgeContent={unreadCount} color="error">
              <IoNotificationsSharp size={24} color={unreadCount > 0 ? "var(--action)" : "var(--green)"} />
            </Badge>
          </span>
          {openNotifications && (
            <div className="notifications-dropdown">
              <div className="header-row">
                <span className="sub-title">{t('notifications')}</span>
                <CircularButton currentTheme={currentTheme} onClick={() => setOpenNotifications(false)} icon={<MdClose />} width={"30px"} height={"30px"} />
              </div>
              <div className="notifications-list">
                {notifications.length === 0 ? (
                  <div className="empty">{t('noNotifications')}</div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n._id}
                      className={`notification-item ${n?.note_opened === "N" ? "unread" : ""}`}
                    >
                      {n.from_photo_url_xx ? <Avatar
                        src={n.from_photo_url_xx || "/img/noavatar.jpg"}
                        alt=""
                        sx={{ width: 32, height: 32 }}
                      />
                        :
                        <InitialsAvatar
                          name={n.from_name_xx}
                          size={30}
                          currentTheme={currentTheme}
                        />}
                      <div className="note-content">
                        <div className="note-text">{n?.note_text}</div>
                        <div className="view-row">
                          <span className="note-meta">{n?.time_ago_xx}</span>
                          {["report", "document"].includes(n.note_type) &&
                            <span className="note-view" onClick={() => handleViewDocument(n)}
                            >{t('view').toUpperCase()}</span>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Theme toggle */}
          <div className="nav-item" onClick={toggleTheme}>
            {currentTheme === "light" && <MdDarkMode color="#000" size={25} style={{ cursor: "pointer" }} />}
            {currentTheme === "dark" && <MdLightMode color="#fff" size={25} style={{ cursor: "pointer" }} />}
          </div>

          {/* User avatar */}
          <div className="avatar">
            <CurrentUserAvatar
              onClick={() => {
                try {
                  setCurrentMenu("/profile");
                  navigate(`/profile`, { replace: true });
                  setCurrentMenuIndex(200);
                } catch (error) {
                  console.log(error);
                }
              }}
            />
          </div>
          <CustomLoadingButton
            onClick={handleLogout}
            height={"36px"}
            width={"120px"}
            currentTheme={currentTheme}
            iconPosition="end"
            borderRadiusType="both"
            tooltip={t("logout")}
            variant="tint"
            color="#E040FB"
          >
            {t("logout")}
          </CustomLoadingButton>
          <div className="icon-container"
            ref={actionsRef}
            onClick={(e) => {
              try {
                e.stopPropagation();
                setOpenActions(!openActions);
              } catch (error) {
                console.log(error);
              }
            }}>
            <MdOutlineMenuOpen className="icon" />
            {openActions && (
              <div className="dropdown" style={{ width: "120px" }}>
                <div className="dropdown-item" onClick={() => navigate("/profile", { replace: true })}>{t('profile')}</div>
                <div className="dropdown-item" onClick={() => setChangePassword(true)}>{t('changePassword')}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {activeStatuses.includes(currentUser?.subscription_status_xx) &&
        <div className="bottom-part">
          <div className="menus-wrapper">
            <CustomButtonMenu
              isCurrent={currentMenuIndex === 100}
              className="single-menu"
              onClick={() => {
                try {
                  handleMenuClick(
                    "/",
                    "/",
                    []
                  )
                  setCurrentMenuIndex(100);
                } catch (error) {
                  console.log(error)
                }
              }}
            >{t('home')}
            </CustomButtonMenu>
            {userRoles.map((role, index) => (
              <div key={role?.ur_role_code}>
                <CustomButtonMenu
                  className="single-menu"
                  isCurrent={index === currentMenuIndex}
                  onClick={() => {
                    try {
                      handleMenuClick(
                        role?.ur_role_code,
                        role?.role_component_xx,
                        role?.role_menus_xx
                      )
                      setCurrentMenuIndex(index);
                    } catch (error) {
                      console.log(error)
                    }
                  }
                  }
                >
                  {role?.role_name_xx}
                </CustomButtonMenu>
              </div>
            ))}
            <CustomButtonMenu
              isCurrent={currentMenuIndex === 200}
              className="single-menu"
              onClick={() => {
                try {
                  setCurrentMenu("/profile");
                  navigate("/profile");
                  setCurrentMenuIndex(200);
                } catch (error) {
                  console.log(error);
                }
              }}
            >{`PROFILE`}</CustomButtonMenu>
            <CustomButtonMenu
              isCurrent={currentMenuIndex === 300}
              className="single-menu"
              onClick={() => {
                try {
                  setCurrentMenu("/menu");
                  navigate("/testmenu");
                  setCurrentMenuIndex(300);
                } catch (error) {
                  console.log(error);
                }
              }}
            >{`TEST`}
            </CustomButtonMenu>
          </div>
        </div>}

      {changePassword &&
        <motion.div
          className="overlay-dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 1 } }}
          exit={{ opacity: 0, transition: { duration: 1 } }}
        >
          <div className="overlay-wrapper">
            <div className="overlay-navbar">
              <span className="main-title">{t('changePassword')}</span>
              <CircularButton currentTheme={currentTheme} onClick={() => setChangePassword(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
            </div>
            <div className="overlay-content">
              <div className="welcome-message">
                <ResetPassword onSuccess={() => setChangePassword(false)} />
              </div>
            </div>
          </div>
        </motion.div>}
      {/* <hr className='separator-h yellow' /> */}
    </div>
  );
};

export default HorizontalNavbar;
